import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardApi, clientsApi, formatCurrency } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { 
  FileText, 
  Users, 
  TrendingUp, 
  Plus,
  ArrowRight,
  Clock
} from 'lucide-react';
import { toast } from 'sonner';

const DashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, clientsRes] = await Promise.all([
        dashboardApi.getStats(),
        clientsApi.getAll()
      ]);
      setStats(statsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 animate-pulse">
        <div className="h-8 w-48 bg-zinc-200 rounded mb-8" />
        <div className="grid grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 bg-zinc-200 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  const statCards = [
    {
      label: 'Total Invoices',
      value: stats?.total_invoices || 0,
      icon: FileText,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
    },
    {
      label: 'Total Clients',
      value: stats?.total_clients || 0,
      icon: Users,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
    },
    ...Object.entries(stats?.revenue_by_currency || {}).map(([currency, amount]) => ({
      label: `Revenue (${currency})`,
      value: formatCurrency(amount, currency),
      icon: TrendingUp,
      color: 'text-green-600',
      bg: 'bg-green-50',
      isCurrency: true,
    })),
  ];

  // Ensure we have at least 4 cards
  while (statCards.length < 4) {
    statCards.push({
      label: 'Revenue',
      value: '$0.00',
      icon: TrendingUp,
      color: 'text-green-600',
      bg: 'bg-green-50',
      isCurrency: true,
    });
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-zinc-900">
            Welcome back, {user?.company_name?.split(' ')[0] || 'there'}
          </h1>
          <p className="text-zinc-500 mt-1">Here's your invoicing overview</p>
        </div>
        <Button 
          onClick={() => navigate('/invoices/create')}
          className="bg-orange-500 hover:bg-orange-600"
          data-testid="create-invoice-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Invoice
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.slice(0, 4).map((stat, index) => (
          <div 
            key={index} 
            className="bg-white border border-zinc-200 rounded-xl p-6 animate-fade-in"
            style={{ animationDelay: `${index * 0.1}s` }}
            data-testid={`stat-card-${index}`}
          >
            <div className={`w-10 h-10 rounded-lg ${stat.bg} flex items-center justify-center mb-4`}>
              <stat.icon className={`w-5 h-5 ${stat.color}`} />
            </div>
            <p className={`text-2xl font-bold ${stat.isCurrency ? 'font-mono' : 'font-heading'} text-zinc-900`}>
              {stat.value}
            </p>
            <p className="text-sm text-zinc-500 mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Recent Invoices & Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Invoices */}
        <div className="lg:col-span-2 bg-white border border-zinc-200 rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-lg font-semibold">Recent Invoices</h2>
            <Button 
              variant="ghost" 
              onClick={() => navigate('/invoices')}
              className="text-zinc-500 hover:text-zinc-900"
              data-testid="view-all-invoices-btn"
            >
              View All
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>

          {stats?.recent_invoices?.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_invoices.map((invoice) => {
                const client = clients.find(c => c.id === invoice.client_id);
                return (
                  <div 
                    key={invoice.id}
                    onClick={() => navigate(`/invoices/${invoice.id}`)}
                    className="flex items-center justify-between p-4 rounded-lg hover:bg-zinc-50 cursor-pointer transition-colors"
                    data-testid={`recent-invoice-${invoice.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-zinc-100 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-zinc-500" />
                      </div>
                      <div>
                        <p className="font-medium text-zinc-900 font-mono text-sm">
                          {invoice.invoice_number}
                        </p>
                        <p className="text-sm text-zinc-500">{client?.name || 'Unknown Client'}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-mono font-semibold text-zinc-900">
                        {formatCurrency(invoice.total, invoice.currency)}
                      </p>
                      <p className="text-xs text-zinc-400">{invoice.invoice_date}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
              <p className="text-zinc-500">No invoices yet</p>
              <Button 
                onClick={() => navigate('/invoices/create')}
                variant="outline"
                className="mt-4"
                data-testid="create-first-invoice-btn"
              >
                Create your first invoice
              </Button>
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-zinc-900 text-white rounded-xl p-6 grain-texture">
          <h2 className="font-heading text-lg font-semibold mb-6">Quick Actions</h2>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/invoices/create')}
              className="w-full flex items-center gap-3 p-4 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-left"
              data-testid="quick-create-invoice"
            >
              <FileText className="w-5 h-5" />
              <span>Create Invoice</span>
            </button>
            <button
              onClick={() => navigate('/clients')}
              className="w-full flex items-center gap-3 p-4 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-left"
              data-testid="quick-manage-clients"
            >
              <Users className="w-5 h-5" />
              <span>Manage Clients</span>
            </button>
            <button
              onClick={() => navigate('/bank-accounts')}
              className="w-full flex items-center gap-3 p-4 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-left"
              data-testid="quick-bank-accounts"
            >
              <TrendingUp className="w-5 h-5" />
              <span>Bank Accounts</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
